/**
 * A conversation as turns: your message, then the reply as rounds (what the model thought and
 * said, then the tools it called), its plan when it kept one (TodoWrite), and its answer (the
 * words after its last tool call). Subagent work nests under the Task that started it. Pure,
 * so it is tested (__tests__/turns.test.ts).
 */
import type { AssistantEntry, AssistantReply, AssistantStep, Conversation } from '@orgus/protocol';
import { countedAction, phaseOf, sentence, stepSentence, todosOf, toolFamily, toolName } from './labels.ts';
import type { Action, Phase, Todo, ToolFamily } from './labels.ts';

/** One tool call, with the calls its subagent made and the subagent's last words. */
export interface StepNode {
  entry: AssistantEntry;
  step: AssistantStep;
  children: StepNode[];
  /** A subagent's final words (Task / Agent). */
  report: string | null;
}

/** One model round: its thinking, what it said, then the tools it called. */
export interface Round {
  key: string;
  thinking: AssistantEntry[];
  /** The words before its tool calls (the model's narration). */
  narration: string;
  steps: StepNode[];
}

export type SectionState = 'done' | 'current' | 'upcoming';

export interface PlanSection {
  key: string;
  title: string;
  /** The present-tense form TodoWrite gives ("Running the tests"). */
  activeForm: string | null;
  state: SectionState;
  items: StepNode[];
}

export interface Plan {
  sections: PlanSection[];
  /** Calls before the first plan. */
  before: StepNode[];
  /** Calls under steps the final plan no longer has. */
  other: StepNode[];
  total: number;
  done: number;
  /** Index of the step in progress, or -1. */
  current: number;
}

export interface Turn {
  key: string;
  /** Your message, or null for entries before any message (or a recap). */
  user: AssistantEntry | null;
  runId: string | null;
  rounds: Round[];
  /** Every top-level call, in order. */
  steps: StepNode[];
  /** The reply's answer: its words after the last call. */
  answer: string;
  /** Errors and notes (stopped, compacted), in order. */
  errors: AssistantEntry[];
  notes: AssistantEntry[];
  /** Calls waiting for a person, anywhere in the turn. */
  waiting: StepNode[];
  plan: Plan | null;
  reply: AssistantReply | null;
  /** The reply is still being written. */
  live: boolean;
  at: number;
  lastAt: number;
}

/** The subagent call an entry belongs to: on the entry for words, on the step for calls. */
const parentOf = (e: AssistantEntry): string | null => e.parentToolUseId ?? e.step?.parentToolUseId ?? null;
const isTop = (e: AssistantEntry) => !parentOf(e);

function newRound(i: number, turnKey: string): Round {
  return { key: `${turnKey}:r${i}`, thinking: [], narration: '', steps: [] };
}

/** The conversation's turns, oldest first. */
export function buildTurns(c: Pick<Conversation, 'entries' | 'replies' | 'runId' | 'state'>): Turn[] {
  const turns: Turn[] = [];
  const replies = new Map((c.replies ?? []).map((r) => [r.runId, r]));
  let t: Turn | null = null;
  let round: Round | null = null;
  let nodes = new Map<string, StepNode>();

  const open = (user: AssistantEntry | null, at: number): Turn => {
    const key = user?.id ?? `t${turns.length}`;
    const turn: Turn = { key, user, runId: null, rounds: [], steps: [], answer: '', errors: [], notes: [], waiting: [], plan: null, reply: null, live: false, at, lastAt: at };
    turns.push(turn);
    round = null;
    nodes = new Map();
    return turn;
  };

  for (const e of c.entries) {
    if (e.kind === 'user') {
      t = open(e, e.at);
      continue;
    }
    t ??= open(null, e.at);
    t.lastAt = Math.max(t.lastAt, e.at);
    if (e.runId && !t.runId) t.runId = e.runId;
    if (e.kind === 'error') {
      t.errors.push(e);
      continue;
    }
    if (e.kind === 'note') {
      t.notes.push(e);
      continue;
    }
    if (!isTop(e)) {
      const parent = nodes.get(parentOf(e)!);
      if (e.kind === 'step' && e.step) {
        const node: StepNode = { entry: e, step: e.step, children: [], report: null };
        if (e.step.toolUseId) nodes.set(e.step.toolUseId, node);
        if (parent) parent.children.push(node);
        else {
          // A subagent we never saw start: show its call at the top level rather than lose it.
          round ??= newRound(t.rounds.length, t.key);
          if (!t.rounds.includes(round)) t.rounds.push(round);
          round.steps.push(node);
          t.steps.push(node);
        }
      } else if (e.kind === 'text' && e.text && parent) parent.report = e.text;
      continue;
    }
    // A model round starts with thinking or words; a new one once the last has called tools.
    if ((e.kind === 'thinking' || e.kind === 'text') && (!round || round.steps.length > 0)) {
      round = newRound(t.rounds.length, t.key);
      t.rounds.push(round);
    }
    if (e.kind === 'thinking') round!.thinking.push(e);
    else if (e.kind === 'text') round!.narration = round!.narration ? `${round!.narration}\n\n${e.text ?? ''}` : (e.text ?? '');
    else if (e.kind === 'step' && e.step) {
      if (!round) {
        round = newRound(t.rounds.length, t.key);
        t.rounds.push(round);
      }
      const node: StepNode = { entry: e, step: e.step, children: [], report: null };
      if (e.step.toolUseId) nodes.set(e.step.toolUseId, node);
      round.steps.push(node);
      t.steps.push(node);
    }
  }

  // A reply that has written nothing yet still belongs to your newest message: it is live
  // from the moment it starts, so the page says "Thinking" instead of showing nothing.
  const newest = turns[turns.length - 1];
  if (newest && !newest.runId && newest.user && c.runId && (c.state === 'running' || c.state === 'waiting')) newest.runId = c.runId;

  for (const turn of turns) {
    // The words after the last call are the answer, not narration.
    const last = turn.rounds[turn.rounds.length - 1];
    if (last && !last.steps.length && last.narration) {
      turn.answer = last.narration;
      last.narration = '';
      if (!last.thinking.length) turn.rounds.pop();
    }
    turn.reply = turn.runId ? (replies.get(turn.runId) ?? null) : null;
    turn.live = !!turn.runId && turn.runId === c.runId && (c.state === 'running' || c.state === 'waiting');
    const walk = (ns: StepNode[]) => {
      for (const n of ns) {
        if (n.step.outcome === 'pending') turn.waiting.push(n);
        walk(n.children);
      }
    };
    walk(turn.steps);
    turn.plan = planOf(turn.steps);
  }
  return turns;
}

// ---------------------------------------------------------------------------
// Plan
// ---------------------------------------------------------------------------

const isPlanCall = (n: StepNode) => toolName(n.step) === 'TodoWrite';

/**
 * The plan the reply kept with TodoWrite, its last version, with each other call filed under
 * the step that was in progress when it ran (between steps: the step just finished).
 */
export function planOf(steps: readonly StepNode[]): Plan | null {
  let latest: Todo[] | null = null;
  let active: string | null = null;
  let lastActive: string | null = null;
  const before: StepNode[] = [];
  const buckets = new Map<string, StepNode[]>();
  for (const n of steps) {
    if (isPlanCall(n)) {
      if (n.step.outcome === 'error' || n.step.outcome === 'denied') continue;
      const todos = todosOf(n.step.input);
      if (!todos.length) continue;
      latest = todos;
      const cur = todos.find((x) => x.status === 'in_progress');
      active = cur ? cur.content : null;
      if (active) lastActive = active;
      continue;
    }
    const home = active ?? lastActive;
    if (!latest || !home) before.push(n);
    else buckets.set(home, [...(buckets.get(home) ?? []), n]);
  }
  if (!latest) return null;
  const titles = new Set(latest.map((x) => x.content));
  const sections = latest.map(
    (x, i): PlanSection => ({
      key: `p${i}`,
      title: x.content,
      activeForm: x.activeForm ?? null,
      state: x.status === 'completed' ? 'done' : x.status === 'in_progress' ? 'current' : 'upcoming',
      items: buckets.get(x.content) ?? [],
    }),
  );
  const other = [...buckets.entries()].filter(([k]) => !titles.has(k)).flatMap(([, v]) => v);
  return { sections, before, other, total: sections.length, done: sections.filter((s) => s.state === 'done').length, current: sections.findIndex((s) => s.state === 'current') };
}

/** The plan in one line: "Step 2 of 5: Run the tests", "All 4 steps done", "3 of 4 steps done". */
export function planHeadline(p: Plan, live: boolean): string {
  if (p.current >= 0 && live) {
    const s = p.sections[p.current]!;
    return `Step ${p.current + 1} of ${p.total}: ${s.title}`;
  }
  if (p.done === p.total) return p.total === 1 ? 'The one step is done' : `All ${p.total} steps done`;
  if (p.current >= 0) return `Step ${p.current + 1} of ${p.total}: ${p.sections[p.current]!.title}`;
  return `${p.done} of ${p.total} steps done`;
}

// ---------------------------------------------------------------------------
// Groups and summaries
// ---------------------------------------------------------------------------

/** The action a call stands for when counted with others like it: "Read a file", "Searched the web". */
function genericAction(n: StepNode): Action | null {
  switch (toolName(n.step)) {
    case 'Read':
      return { plain: 'read', object: 'a file' };
    case 'Write':
      return { plain: 'write', object: 'a file' };
    case 'Edit':
    case 'MultiEdit':
      return { plain: 'edit', object: 'a file' };
    case 'Grep':
    case 'Glob':
    case 'LS':
      return { plain: 'search', object: 'files' };
    case 'WebSearch':
      return { plain: 'search', object: 'the web' };
    case 'WebFetch':
      return { plain: 'read', object: 'a page' };
    case 'Bash':
    case 'PowerShell':
      return { plain: 'run', object: 'a command' };
    case 'Task':
    case 'Agent':
      return { plain: 'ask', object: 'an agent' };
    default:
      return null;
  }
}

const phaseClass = (p: Phase) => (p === 'done' ? 'done' : p);

export interface StepGroup {
  key: string;
  /** "Read 4 files", "Searched the web", or the one call's own sentence. */
  label: string;
  /** "×3" when the label could not say how many. */
  times: number | null;
  nodes: StepNode[];
  failed: number;
}

/**
 * Consecutive calls of one tool in one state become one row: "Read 4 files", "Searched the
 * web ×3". Calls waiting for a person, plans and subagents always stand alone.
 */
export function groupSteps(nodes: readonly StepNode[]): StepGroup[] {
  const out: StepGroup[] = [];
  for (const n of nodes) {
    const phase = phaseClass(phaseOf(n.step));
    const generic = genericAction(n);
    const last = out[out.length - 1];
    const prev = last?.nodes[last.nodes.length - 1];
    const joins =
      !!last && !!prev && !!generic && phase !== 'waiting' && n.children.length === 0 && prev.children.length === 0 && toolName(prev.step) === toolName(n.step) && phaseClass(phaseOf(prev.step)) === phase;
    if (joins) {
      last!.nodes.push(n);
      const base = sentence(generic!, phase as Phase);
      const counted = countedAction(base, last!.nodes.length);
      last!.label = counted ?? base;
      last!.times = counted ? null : last!.nodes.length;
      if (n.step.outcome === 'error') last!.failed++;
    } else {
      out.push({ key: n.entry.id, label: stepSentence(n.step), times: null, nodes: [n], failed: n.step.outcome === 'error' ? 1 : 0 });
    }
  }
  return out;
}

/** The clause one family of work adds to a summary, for n calls. */
function clause(f: ToolFamily, n: number, nodes: StepNode[]): string | null {
  const times = (one: string, many: string) => (n === 1 ? one : many);
  switch (f) {
    case 'read':
      return times('read a file', `read ${n} files`);
    case 'write':
      return times('changed a file', `changed ${n} files`);
    case 'search':
      return times('searched files', `searched files ${n} times`);
    case 'web': {
      const s = nodes.filter((x) => toolName(x.step) === 'WebSearch').length;
      const p = n - s;
      return [s ? (s === 1 ? 'searched the web' : `searched the web ${s} times`) : null, p ? (p === 1 ? 'read a page' : `read ${p} pages`) : null].filter(Boolean).join(' · ');
    }
    case 'shell':
      return times('ran a command', `ran ${n} commands`);
    case 'agent':
      return times('asked an agent', `asked ${n} agents`);
    case 'skill':
      return times('used a skill', `used ${n} skills`);
    case 'orgus-read':
      return times('checked Orgus', `checked Orgus ${n} times`);
    case 'orgus-change': {
      const label = stepSentence(nodes[0]!.step);
      const counted = n > 1 && nodes.every((x) => x.step.capability === nodes[0]!.step.capability) ? countedAction(label, n) : null;
      const one = (counted ?? (n === 1 ? label : `made ${n} changes in Orgus`)).replace(/^\p{Lu}/u, (c) => c.toLowerCase());
      return one;
    }
    case 'plan':
      return null;
    default:
      return times('used a tool', `used ${n} tools`);
  }
}

/** What a reply did, in one line: "Read 4 files · ran 2 commands · stopped a session". */
export function describeSteps(steps: readonly StepNode[]): string {
  const work = steps.filter((n) => !isPlanCall(n));
  if (!work.length) return steps.length ? 'Kept a plan' : 'Answered without tools';
  const failed = work.filter((n) => n.step.outcome === 'error');
  const refused = work.filter((n) => n.step.outcome === 'denied' || n.step.outcome === 'expired');
  const done = work.filter((n) => !failed.includes(n) && !refused.includes(n) && n.step.outcome !== 'pending');
  const order: ToolFamily[] = [];
  const byFamily = new Map<ToolFamily, StepNode[]>();
  for (const n of done) {
    const f = toolFamily(n.step);
    if (!byFamily.has(f)) order.push(f);
    byFamily.set(f, [...(byFamily.get(f) ?? []), n]);
  }
  const clauses = order.map((f) => clause(f, byFamily.get(f)!.length, byFamily.get(f)!)).filter((x): x is string => !!x);
  const shown = clauses.slice(0, 3);
  if (clauses.length > 3) shown.push(`and ${clauses.length - 3} other ${clauses.length - 3 === 1 ? 'thing' : 'things'}`);
  if (failed.length === 1) shown.push(stepSentence(failed[0]!.step).replace(/^C/, 'c'));
  else if (failed.length > 1) shown.push(`couldn't finish ${failed.length} steps`);
  if (refused.length) shown.push(refused.length === 1 ? stepSentence(refused[0]!.step).replace(/^D/, 'd') : `didn't go ahead with ${refused.length} steps`);
  const waiting = work.filter((n) => n.step.outcome === 'pending').length;
  if (waiting) shown.push(waiting === 1 ? 'one step waits for you' : `${waiting} steps wait for you`);
  const line = shown.join(' · ');
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/** What is happening right now in a live turn: the running call's sentence, or thinking or writing. */
export function nowDoing(turn: Turn, live: { text: string; thinking: string; tool: string | null } | null): string {
  if (turn.waiting.length) return turn.waiting.length === 1 ? 'Waiting for your approval' : `Waiting for your approval on ${turn.waiting.length} steps`;
  const running: StepNode[] = [];
  const walk = (ns: StepNode[]) => {
    for (const n of ns) {
      if (n.step.outcome === 'running') running.push(n);
      walk(n.children);
    }
  };
  walk(turn.steps);
  if (running.length) {
    const last = running[running.length - 1]!;
    const more = running.length - 1;
    return `${stepSentence(last.step)}${more ? `, and ${more} more` : ''}`;
  }
  if (live?.tool) return 'Preparing a step';
  if (live?.text) return 'Writing the answer';
  return 'Thinking';
}

/** Inline Markdown as plain words, for one-line places: **bold**, `code`, [link](url). */
export function plainInline(s: string): string {
  return s
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(^|[^\w])[*_]([^*_\n]+)[*_](?=[^\w]|$)/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/, '');
}

/** The first sentence of a round's narration, for its heading (plain words), and the rest. */
export function splitNarration(text: string): { head: string; rest: string } {
  const t = text.trim();
  const m = /^([^\n]*?[.!?])(\s+)([\s\S]*)$/.exec(t);
  const line = t.split('\n')[0]!;
  if (m && m[1]!.length <= line.length) return { head: plainInline(m[1]!).replace(/\.$/, ''), rest: m[3]!.trim() };
  return { head: plainInline(line).replace(/\.$/, ''), rest: t.slice(line.length).trim() };
}

/** How long a set of calls took, start of the first to end of the last. */
export function spanMs(nodes: readonly StepNode[], now: number): number | null {
  const starts = nodes.map((n) => n.step.startedAt ?? n.entry.at).filter((x) => Number.isFinite(x));
  if (!starts.length) return null;
  const ends = nodes.map((n) => n.step.endedAt ?? (n.step.outcome === 'running' ? now : (n.step.startedAt ?? n.entry.at) + (n.step.ms ?? 0)));
  return Math.max(0, Math.max(...ends) - Math.min(...starts));
}

/** All of a turn's calls, subagent work included. */
export function allSteps(turn: Pick<Turn, 'steps'>): StepNode[] {
  const out: StepNode[] = [];
  const walk = (ns: StepNode[]) => {
    for (const n of ns) {
      out.push(n);
      walk(n.children);
    }
  };
  walk(turn.steps);
  return out;
}
